import { useSubscription } from '@apollo/client';
import {
  Avatar,
  Button,
  Dropdown,
  Icons,
  List,
  Menu,
  notification,
  Radio,
  Select,
  Space,
  Table,
  TableProps,
  Tag,
} from '@pankod/refine-antd';
import { Grid } from 'antd';
import { IResourceComponentsProps } from '@pankod/refine-core';
import defaultAvatar from 'assets/images/default-avatar.jpg';
import { SearchWithDateRange } from 'components/search/SearchWithDateRange';
import { EStatus } from 'enums';
import { IQueryOptionals } from 'interfaces';
import {
  calculateWaitingTime,
  formatTime,
  getTrackingStatusColor,
  STATUS_MAPPING_REVERSE,
  WAITING_TIME_THRESHOLD,
} from 'pages/restaurant-sales/list';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAllCompanyConfig, getCompanyInfo } from 'services/config';
import { SearchIndex } from 'services/meilisearch';
import {
  getKitchenSales,
  getSalesAndProductStatusCount,
  SalesAndProductStatusCountResponse,
  updateSaleProductStatus,
} from 'services/sale';
import { MESSAGE_ADDED_SUBSCRIPTION } from 'services/subscription';
import { getAllTagDropdown } from 'services/tag';
import MetaData from 'utilities/metadata';
import {
  capitalize,
  formatToLocalDateTime,
  getDateRangeOfLastXDays,
  OrderTypeMapping,
} from 'utilities/shared';
import styles from './style.module.css';
import { EOrderType } from 'services/pos';
import { useUserData } from 'hooks';

type StatusType = 'all' | 'new' | 'processing' | 'completed';
type IQuery = IQueryOptionals & { statuses?: EStatus[]; tag?: string };

type KitchenType = {
  salesProductId: string;
  orderId: string;
  orderType: string;
  productImage: string | null;
  tags: string[];
  productSku: string;
  productName: string;
  productQuantity: number;
  salesDate: Date;
  completedAt: Date;
  salesProductStatus: EStatus;
  trackingStatus: string;
};

const getTrackingStatus = (sale: Omit<KitchenType, 'trackingStatus'>) => {
  const waitingTime = calculateWaitingTime(sale);
  let trackingStatus = 'New';

  if (sale.salesProductStatus === EStatus.CONFIRMED && waitingTime <= WAITING_TIME_THRESHOLD) {
    trackingStatus = 'New';
  } else if (
    sale.salesProductStatus === EStatus.CONFIRMED &&
    waitingTime > WAITING_TIME_THRESHOLD
  ) {
    trackingStatus = 'Pending';
  } else if (sale.salesProductStatus === EStatus.IN_PROGRESS) {
    trackingStatus = 'Processing';
  } else if (sale.salesProductStatus === EStatus.COMPLETED) {
    trackingStatus = 'Completed';
  }

  return trackingStatus;
};

export const KitchenOrderList: React.FC<IResourceComponentsProps> = () => {
  const breakpoint = Grid.useBreakpoint();
  const isNarrowScreen = !breakpoint.lg;
  const [sales, setSales] = useState<KitchenType[]>([]);
  const [totalSales, setTotalSales] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>(() => {
    const [startDate, endDate] = getDateRangeOfLastXDays(0);
    return {
      startDate: formatToLocalDateTime(startDate, 0, 0), // 12:00 AM
      endDate: formatToLocalDateTime(endDate, 23, 59), // 11:59 PM
    };
  });
  const [status, setStatus] = useState<StatusType>('all');
  const [tags, setTags] = useState<{ id: string; name: string }[]>([]);
  const [selectedTag, setSelectedTag] = useState<string>();
  const [avatar, setAvatar] = useState<string>();
  const isInitialMount = useRef(true);
  const [subscriptionData, setSubscriptionData] = useState<any>(null);
  const { data: newSubscriptionData } = useSubscription(MESSAGE_ADDED_SUBSCRIPTION);
  const [statusCount, setStatusCount] = useState<SalesAndProductStatusCountResponse | null>(null);
  const userData = useUserData();

  useEffect(() => {
    const timer = setInterval(() => {
      setSales((prevSales) =>
        prevSales.map((sale) => {
          const trackingStatus = getTrackingStatus(sale);
          return { ...sale, trackingStatus };
        })
      );
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const query = useMemo<IQuery>(() => {
    const query: IQuery = {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      searchString: searchText,
      statuses: status === 'all' ? undefined : [STATUS_MAPPING_REVERSE[capitalize(status)]],
      tag: selectedTag === 'all' ? undefined : selectedTag,
      start: page,
      limit,
    };

    return query;
  }, [dateRange.startDate, dateRange.endDate, searchText, selectedTag, status, page, limit]);

  const fetchSales = useCallback(async (query: IQuery) => {
    setIsLoading(true);
    try {
      const response = await getKitchenSales(query);
      const kitchenSales = response.data.map((item) => {
        const result: Omit<KitchenType, 'trackingStatus'> = {
          salesProductId: item.id,
          orderId: item.salesCopy.id,
          orderType: item.salesCopy.orderType,
          productImage: item.productVariant?.photo?.url ?? null,
          tags: item.productVariant?.productTags?.map((tag) => tag.tag.name) ?? [],
          productSku: item.productVariant.sku,
          productName: item.productVariant.name,
          productQuantity: item.quantity,
          salesDate: new Date(item.salesCopy.salesDate),
          completedAt: new Date(item.salesCopy.salesCompletedAt),
          salesProductStatus: item.salesProductStatus as EStatus,
        };
        const trackingStatus = getTrackingStatus(result);
        return { ...result, trackingStatus };
      });
      setSales(kitchenSales);
      setTotalSales(response.total);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err?.message || 'Failed to fetch kitchen sales',
      });
    }
    setIsLoading(false);
  }, []);

  const fetchStatusCount = useCallback(async () => {
    try {
      const response = await getSalesAndProductStatusCount({
        startDate: query.startDate,
        endDate: query.endDate,
        tag: query.tag,
      });
      setStatusCount(response);
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err?.message || 'Failed to fetch status count',
      });
    }
  }, [query.endDate, query.startDate, query.tag]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getAllCompanyConfig();
        const configId = data[0].id;
        const companyInfo = await getCompanyInfo(configId);
        setAvatar(companyInfo.companyConfigPhoto[0]?.avatar?.url);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err?.message || 'Failed to load company logo',
        });
      }
    })();
  }, []);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        setIsLoading(true);
        const tags = await getAllTagDropdown();
        const tagOptions = [{ id: 'all', name: 'All' }].concat(
          tags.map((tag) => ({ id: tag.name, name: tag.name }))
        );
        setTags(tagOptions);
        setSelectedTag(tagOptions[0]?.id ?? undefined);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err?.message || 'Failed to fetch tags',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchTags();
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    fetchSales(query);
  }, [fetchSales, query]);

  useEffect(() => {
    fetchStatusCount();
  }, [fetchStatusCount]);

  useEffect(() => {
    if (newSubscriptionData) {
      setSubscriptionData(newSubscriptionData); // Update state when new data arrives
    }
  }, [newSubscriptionData]);

  // Update the kitchen list when new data is received
  useEffect(() => {
    if (subscriptionData && subscriptionData.messageAdded) {
      let parsedData: { instanceId: string; message: string } = {
        instanceId: '',
        message: '',
      };
      try {
        parsedData = JSON.parse(subscriptionData.messageAdded.content);
      } catch (error) {
        console.error('Error parsing message content:', error);
      }

      // Check if the instanceId matches the current platform ID
      if (!parsedData.instanceId || userData?.user?.platform?.id !== parsedData.instanceId) {
        return;
      }

      notification.success({
        message: 'Message',
        description: `${parsedData.message}`,
      });
      fetchSales(query); // Fetch latest sales
      fetchStatusCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionData, query]);

  const getButtonStyle = (value: StatusType, bgColor: string) => ({
    backgroundColor: status === value ? undefined : bgColor, // Keep default Ant Design primary when selected
  });

  const getCountStyle = (value: StatusType) => ({
    fontSize: 12,
    border: '1px solid #999',
    borderRadius: 100,
    padding: '0 4px',
    color: status === value ? '#fff' : '#000',
    transition: 'all 0.3s ease',
  });

  const handleStatusUpdate = async (id: string, status: EStatus) => {
    try {
      await updateSaleProductStatus(id, status);
      await fetchSales(query);
      await fetchStatusCount();
      notification.success({
        message: 'Success',
        description: 'Successfully changed status',
      });
    } catch (_) {
      notification.error({
        message: 'Failed',
        description: 'Failed to change status',
      });
    }
  };

  const handleTagChange = (value: string) => {
    setSelectedTag(value);
    setPage(1);
    setSales([]);
  };

  const onTableChange: TableProps<KitchenType>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra
  ) => {
    setPage(pagination.current ?? 1);
    setLimit(pagination.pageSize ?? 10);
  };

  const onSearch = async (values: {
    startDate?: string;
    endDate?: string;
    searchString: string;
  }) => {
    setDateRange((prev) => {
      const startDate = values.startDate || prev.startDate;
      const endDate = values.endDate || prev.endDate;

      return {
        startDate: startDate ? formatToLocalDateTime(new Date(startDate), 0, 0) : undefined, // 12:00 AM
        endDate: endDate ? formatToLocalDateTime(new Date(endDate), 23, 59) : undefined, // 11:59 PM
      };
    });
    setSearchText(values.searchString);
    setPage(1);
  };

  return (
    <>
      <MetaData />
      <List
        headerProps={{
          extra: (
            <Space style={{ marginLeft: '2rem' }}>
              <img
                src={avatar || defaultAvatar}
                style={{ objectFit: 'contain' }}
                alt={'test'}
                height={32}
                width={71}
              />
            </Space>
          ),
        }}
      >
        <SearchWithDateRange
          placeholder="Search by Order ID, Client Name"
          size="large"
          className={styles['filter-section__search']}
          index={SearchIndex.Sales}
          groups={['id', 'client']}
          onSearch={onSearch}
          dateRange={getDateRangeOfLastXDays(0)}
        />

        <div
          style={{
            marginBottom: '2rem',
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: isNarrowScreen ? 'column' : 'row',
            alignItems: isNarrowScreen ? 'stretch' : 'center',
            gap: isNarrowScreen ? '0.75rem' : '1rem',
          }}
        >
          <div style={{ width: '100%', overflowX: isNarrowScreen ? 'auto' : 'visible' }}>
            <Radio.Group
              size="middle"
              buttonStyle="solid"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              style={{
                display: 'inline-flex',
                flexWrap: 'nowrap',
                minWidth: 'max-content',
              }}
            >
              <Radio.Button value="all">All</Radio.Button>
              <Radio.Button
                value="new"
                style={getButtonStyle('new', getTrackingStatusColor('new'))}
              >
                New{' '}
                <span style={{ ...getCountStyle('new') }}>
                  {statusCount?.salesProductStatusCount.orderConfirmed ?? 0}
                </span>
              </Radio.Button>
              <Radio.Button
                value="processing"
                style={getButtonStyle('processing', getTrackingStatusColor('processing'))}
              >
                Processing{' '}
                <span style={{ ...getCountStyle('processing') }}>
                  {statusCount?.salesProductStatusCount.deliveryInProgress ?? 0}
                </span>
              </Radio.Button>
              <Radio.Button
                value="completed"
                style={getButtonStyle('completed', getTrackingStatusColor('completed'))}
              >
                Completed{' '}
                <span style={{ ...getCountStyle('completed') }}>
                  {statusCount?.salesProductStatusCount.completed ?? 0}
                </span>
              </Radio.Button>
            </Radio.Group>
          </div>

          <Select
            showSearch
            style={{
              minWidth: isNarrowScreen ? undefined : 200,
              width: isNarrowScreen ? '100%' : undefined,
            }}
            value={selectedTag}
            onChange={handleTagChange}
            options={tags.map((w) => ({ value: w.id, label: w.name }))}
          />
        </div>

        <Table
          bordered
          rowKey="id"
          dataSource={sales}
          loading={isLoading}
          scroll={{ x: isNarrowScreen ? 1300 : 'max-content' }}
          onChange={onTableChange}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            total: totalSales,
            current: page,
            pageSize: limit,
            position: ['bottomCenter'],
          }}
          onRow={(record) => {
            const backgroundColor = getTrackingStatusColor(record.trackingStatus);
            return {
              style: { backgroundColor },
            };
          }}
        >
          <Table.Column<KitchenType> dataIndex="orderId" title="Order ID" />
          <Table.Column<KitchenType>
            dataIndex="orderType"
            title="Order Type"
            render={(value) => {
              if (!value) return null;

              return <Tag>{OrderTypeMapping[value as EOrderType]}</Tag>;
            }}
          />
          <Table.Column<KitchenType>
            title="Item"
            width={40}
            render={(_, record) => (
              <Avatar
                src={record.productImage || 'https://via.placeholder.com/50'}
                shape="square"
                size={40}
              />
            )}
          />
          <Table.Column<KitchenType>
            dataIndex="tags"
            title="Kitchen"
            render={(_, record) => {
              return record.tags.map((item) => (
                <Tag style={{ margin: 2 }} key={item}>
                  {item}
                </Tag>
              ));
            }}
          />
          <Table.Column<KitchenType> dataIndex="productSku" title="Item Code" />
          <Table.Column<KitchenType> dataIndex="productName" title="Item name" width={200} />
          <Table.Column<KitchenType> dataIndex="productQuantity" title="Quantity" />
          <Table.Column<KitchenType>
            dataIndex="trackingStatus"
            title="Status"
            width={120}
            render={(_, record) => {
              let tagColor = '';
              if (record.trackingStatus === 'New') {
                tagColor = 'blue';
              } else if (record.trackingStatus === 'Pending') {
                tagColor = 'magenta';
              } else if (record.trackingStatus === 'Processing') {
                tagColor = 'green';
              }

              return <Tag color={tagColor}>{record.trackingStatus.toUpperCase()}</Tag>;
            }}
          />
          <Table.Column<KitchenType>
            title="WAITING FOR"
            key="waitingTime"
            width={140}
            render={(_: any, record) => {
              if (record.salesProductStatus === EStatus.COMPLETED) {
                const createdTime = new Date(record.salesDate).getTime();
                const completedTime = new Date(record.completedAt).getTime();
                const timeDiff = Math.floor((completedTime - createdTime) / 1000);
                return <span>{formatTime(timeDiff)}</span>;
              }

              const waitingTime = calculateWaitingTime(record);
              return <span>{formatTime(waitingTime)}</span>;
            }}
          />
          <Table.Column<KitchenType>
            title="Actions"
            fixed={isNarrowScreen ? undefined : 'right'}
            dataIndex="actions"
            width={120}
            align="center"
            render={(_, record): React.ReactNode => {
              return <StatusUpdateAction record={record} handleStatusUpdate={handleStatusUpdate} />;
            }}
          />
        </Table>
      </List>
    </>
  );
};

type StatusUpdateActionProps = {
  record: KitchenType;
  handleStatusUpdate: (id: string, status: EStatus) => Promise<void>;
};

const StatusUpdateAction: React.FC<StatusUpdateActionProps> = ({ record, handleStatusUpdate }) => {
  const moreMenu = (record: KitchenType) => (
    <Menu mode="vertical" onClick={({ domEvent }) => domEvent.stopPropagation()}>
      <Menu.Item
        key="confirmed"
        disabled={true} // Always disabled because we should never go back
      >
        New
      </Menu.Item>
      <Menu.Item
        key="progress"
        disabled={record.salesProductStatus !== EStatus.CONFIRMED}
        // Enabled only if the current status is CONFIRMED
        onClick={() =>
          record.salesProductId && handleStatusUpdate(record.salesProductId, EStatus.IN_PROGRESS)
        }
      >
        Processing
      </Menu.Item>
      <Menu.Item
        key="completed"
        disabled={record.salesProductStatus === EStatus.COMPLETED}
        // Enabled only if the current status is CONFIRMED or IN_PROGRESS
        onClick={() =>
          record.salesProductId && handleStatusUpdate(record.salesProductId, EStatus.COMPLETED)
        }
      >
        Completed
      </Menu.Item>
    </Menu>
  );

  return (
    <Dropdown overlay={moreMenu(record)} trigger={['click']}>
      <Button size="small" icon={<Icons.MoreOutlined />} onClick={(e) => e.stopPropagation()} />
    </Dropdown>
  );
};
